import { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate, Link, useLocation } from 'react-router-dom'
import { useAuth } from '../../../contexts/AuthContext'
import { useTheme } from '../../../contexts/ThemeContext'
import { usePortalNavigation } from '../hooks/usePortalNavigation'
import { usePortalClient } from '../hooks/usePortalClient'
import { validatePassword } from '../../../shared/lib/passwordValidation'
import { useDemoPlan } from '../../../hooks/useDemoPlan'
import AccountSwitcher from '../../../shared/components/AccountSwitcher'

function PortalLayout({ children }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, profile, signOut, isAdmin } = useAuth()
  const { isDemoOrg } = useDemoPlan()
  const { darkMode, toggleDarkMode } = useTheme()
  const { tabs, activeTab, isRetainer } = usePortalNavigation()
  const { clientId, client, isAdminPreview } = usePortalClient()

  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const [sidebarPinned, setSidebarPinned] = useState(() => localStorage.getItem('portal-sidebar-pinned') === 'true')
  const [sidebarHovered, setSidebarHovered] = useState(false)
  const sidebarExpanded = sidebarPinned || sidebarHovered
  const sidebarCollapsed = !sidebarExpanded

  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState(false)
  const [changingPassword, setChangingPassword] = useState(false)
  const userMenuRef = useRef(null)

  // Close user menu on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileSidebarOpen(false)
  }, [location.pathname])

  const handleSignOut = async () => {
    setShowUserMenu(false)
    await signOut()
    navigate('/login')
  }

  const passwordValidation = useMemo(() => validatePassword(newPassword), [newPassword])
  const passwordsMatch = newPassword.length > 0 && confirmPassword.length > 0 && newPassword === confirmPassword

  const handlePasswordChange = async (e) => {
    e.preventDefault()
    setPasswordError('')
    setPasswordSuccess(false)

    if (!passwordValidation.valid) {
      setPasswordError('Please fix the password requirements below')
      return
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match')
      return
    }

    setChangingPassword(true)
    try {
      const { supabase } = await import('../../../shared/lib/supabase')
      const { error } = await supabase.auth.updateUser({ password: newPassword })
      if (error) throw error
      setPasswordSuccess(true)
      setNewPassword('')
      setConfirmPassword('')
      setTimeout(() => {
        setShowPasswordModal(false)
        setPasswordSuccess(false)
      }, 2000)
    } catch (err) {
      setPasswordError(err.message || 'Failed to update password')
    } finally {
      setChangingPassword(false)
    }
  }

  const closePasswordModal = () => {
    setShowPasswordModal(false)
    setNewPassword('')
    setConfirmPassword('')
    setPasswordError('')
    setPasswordSuccess(false)
  }

  // Initials for avatar
  const initials = profile?.full_name
    ? profile.full_name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : user?.email?.[0]?.toUpperCase() || 'U'

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile sidebar backdrop */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-900/50 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar — expands on hover */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-full
          ${sidebarExpanded ? 'w-64' : 'w-16'}
          transform transition-all duration-200 ease-in-out
          lg:translate-x-0
          ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        style={{ backgroundColor: '#012438', borderRight: '1px solid #3F5A6A' }}
        onMouseEnter={() => setSidebarHovered(true)}
        onMouseLeave={() => setSidebarHovered(false)}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={`flex items-center h-14 ${sidebarCollapsed ? 'px-3 justify-center' : 'px-6'}`}>
            <Link to={tabs[0]?.path || '/portal'} className={`flex items-center ${sidebarCollapsed ? '' : 'space-x-3'}`}>
              <img src="/logo.png" alt="SSA" className="w-8 h-8 rounded-lg object-contain flex-shrink-0" />
              {!sidebarCollapsed && (
                <div className="flex flex-col">
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-sm font-bold text-white tracking-wide">Vantage</span>
                    <span className="text-[9px] font-normal" style={{ color: '#6B7F8E' }}>by Sidewalk Security</span>
                  </div>
                  <span className="text-[8px] font-medium uppercase tracking-wider" style={{ color: '#8A9BAA' }}>Client Portal</span>
                </div>
              )}
            </Link>
          </div>

          {/* Nav */}
          <nav className={`flex-1 ${sidebarCollapsed ? 'px-2' : 'px-4'} py-4 space-y-1 overflow-y-auto scrollbar-hide`}>
            {tabs.map(tab => {
              const isActive = activeTab?.id === tab.id
              if (tab.comingSoon) {
                return (
                  <div
                    key={tab.id}
                    title={sidebarCollapsed ? `${tab.label} (Soon)` : undefined}
                    className={`
                      flex items-center ${sidebarCollapsed ? 'justify-center px-0 py-2.5' : 'px-3 py-2.5'} rounded-lg text-sm font-medium
                      opacity-50 cursor-not-allowed
                    `}
                    style={{ color: '#5A7080' }}
                  >
                    <span className={`${sidebarCollapsed ? '' : 'mr-3'} flex-shrink-0`} style={{ color: '#5A7080' }}>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={tab.iconPath} />
                      </svg>
                    </span>
                    {!sidebarCollapsed && (
                      <span className="flex items-center gap-2">
                        {tab.label}
                        <span className="text-[9px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider" style={{ backgroundColor: '#213C4E', color: '#5A7080' }}>Soon</span>
                      </span>
                    )}
                  </div>
                )
              }
              return (
                <Link
                  key={tab.id}
                  to={tab.path}
                  title={sidebarCollapsed ? tab.label : undefined}
                  className={`
                    flex items-center ${sidebarCollapsed ? 'justify-center px-0 py-2.5' : 'px-3 py-2.5'} rounded-lg text-sm font-medium
                    transition-all duration-200 relative
                  `}
                  style={isActive
                    ? { backgroundColor: '#213C4E', color: '#ffffff' }
                    : { color: '#BCC5CD' }
                  }
                  onMouseEnter={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = '#213C4E'; e.currentTarget.style.color = '#ffffff' } }}
                  onMouseLeave={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#BCC5CD' } }}
                >
                  <span className={`${sidebarCollapsed ? '' : 'mr-3'} transition-colors flex-shrink-0`}
                    style={{ color: isActive ? '#8AD0D6' : '#7E8F9C' }}>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={tab.iconPath} />
                    </svg>
                  </span>
                  {!sidebarCollapsed && (
                    <>
                      {tab.label}
                      {isActive && (
                        <span className="ml-auto w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#8AD0D6' }} />
                      )}
                    </>
                  )}
                </Link>
              )
            })}
          </nav>

          {/* Account Switcher (Global Admin) or Back to Admin link */}
          {profile?.role === 'global_admin' ? (
            <AccountSwitcher collapsed={sidebarCollapsed} currentView="portal" />
          ) : isAdmin ? (
            <div className={`${sidebarCollapsed ? 'px-2' : 'px-4'} py-3`}>
              <Link
                to={isAdminPreview && clientId ? `/clients/${clientId}?tab=admin` : '/'}
                title={sidebarCollapsed ? (isAdminPreview ? 'Back to Admin' : 'Admin Portal') : undefined}
                className={`
                  flex items-center ${sidebarCollapsed ? 'justify-center px-0 py-2.5' : 'px-3 py-2.5'} rounded-lg text-sm font-medium
                  transition-all duration-200
                `}
                style={{ color: '#8AD0D6' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#213C4E' }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent' }}
              >
                <svg className={`w-5 h-5 ${sidebarCollapsed ? '' : 'mr-3'} flex-shrink-0`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                {!sidebarCollapsed && (isAdminPreview ? 'Back to Admin' : 'Admin Portal')}
              </Link>
            </div>
          ) : null}

          {/* Expand / Collapse toggle */}
          <div className={`${sidebarCollapsed ? 'px-2' : 'px-4'} py-3`}>
            <div className="mx-2 mb-2" style={{ borderTop: '1px solid rgba(63, 90, 106, 0.4)' }} />
            <button
              onClick={() => {
                const next = !sidebarPinned
                setSidebarPinned(next)
                localStorage.setItem('portal-sidebar-pinned', next ? 'true' : 'false')
                if (!next) setSidebarHovered(false)
              }}
              className="w-full flex items-center justify-center px-3 py-2 rounded-lg text-sm transition-colors"
              style={{ color: '#7E8F9C' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#213C4E'; e.currentTarget.style.color = '#BCC5CD' }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#7E8F9C' }}
              title={sidebarPinned ? 'Collapse sidebar' : 'Sidebar control'}
            >
              {sidebarCollapsed ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <line x1="9" y1="3" x2="9" y2="21" />
                </svg>
              ) : (
                <>
                  <svg className={`w-5 h-5 transition-transform duration-300 ${sidebarPinned ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  </svg>
                  <span className="ml-3">{sidebarPinned ? 'Collapse' : 'Expand'}</span>
                </>
              )}
            </button>
          </div>

        </div>
      </aside>

      {/* Main area */}
      <div className={`${sidebarExpanded ? 'lg:pl-64' : 'lg:pl-16'} flex flex-col min-h-screen transition-all duration-200`}>
        {/* Demo mode banner */}
        {isDemoOrg && (
          <div className="sticky top-0 z-40 flex items-center justify-center gap-2 px-4 py-1.5 text-xs font-medium" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            You are using a Demo account. Some features are restricted.
            <a href="mailto:support@sidewalksecurity.com" className="underline hover:no-underline font-semibold">Contact us to upgrade</a>
          </div>
        )}
        {/* Top header */}
        <header className={`sticky ${isDemoOrg ? 'top-[30px]' : 'top-0'} z-30 bg-white/80 dark:bg-gray-900 backdrop-blur-md dark:backdrop-blur-none border-b border-gray-200 dark:border-gray-700`}>
          <div className="flex items-center justify-between h-16 px-4 sm:px-6">
            {/* Left: hamburger + org name */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setMobileSidebarOpen(true)}
                className="lg:hidden p-2 -ml-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <div className="hidden lg:flex flex-col">
                <span className="text-[11px] font-medium uppercase tracking-widest text-gray-400 dark:text-gray-500">
                  {client?.name || 'Client Portal'}
                </span>
                <span className="text-[9px] font-medium uppercase tracking-wider text-gray-300 dark:text-gray-600">
                  Client Portal
                </span>
              </div>
            </div>

            {/* Right: dark mode + user */}
            <div className="flex items-center space-x-2">
              {/* Dark mode toggle */}
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {darkMode ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                )}
              </button>

              {/* User avatar + dropdown */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <span className="text-sm font-medium hidden sm:block text-gray-900 dark:text-gray-200">
                    {profile?.full_name || user?.email}
                  </span>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold bg-gradient-to-br from-primary-400 to-primary-600">
                    {initials}
                  </div>
                </button>

                {showUserMenu && (
                  <div className="absolute right-0 mt-2 min-w-56 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50">
                    <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                      <p className="text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">{profile?.full_name || 'User'}</p>
                      {profile?.role && (
                        <p className="text-[11px] font-medium text-primary-600 dark:text-primary-400 mt-0.5 whitespace-nowrap">
                          {profile.role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 whitespace-nowrap">{user?.email}</p>
                    </div>
                    <div className="py-1">
                      <button
                        onClick={() => { setShowUserMenu(false); setShowPasswordModal(true) }}
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                        </svg>
                        Change Password
                      </button>
                      <button
                        onClick={handleSignOut}
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Sign out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Scrollable content area */}
        <main className="flex-1 px-6 py-7">
          <div className="max-w-[860px] mx-auto">
            {/* Admin preview banner */}
            {isAdminPreview && (
              <div className="flex items-center justify-between p-3 rounded-lg mb-5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-blue-500 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Previewing client portal</span>
                </div>
                <Link
                  to={`/clients/${clientId}?tab=admin`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/40 hover:bg-blue-200 dark:hover:bg-blue-900/60"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Back to Admin
                </Link>
              </div>
            )}
            {children}
          </div>
        </main>

        {/* Footer */}
        <footer className="px-6 py-4">
          <p className="text-center text-xs text-gray-400 dark:text-gray-500">
            Sidewalk Security Advisors • Executive Advisory Platform
          </p>
        </footer>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Change Password</h2>
              <button onClick={closePasswordModal} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {passwordSuccess ? (
              <div className="text-center py-4">
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-green-600 dark:text-green-400 font-medium">Password updated successfully!</p>
              </div>
            ) : (
              <form onSubmit={handlePasswordChange} className="space-y-4">
                {passwordError && (
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
                    {passwordError}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter new password"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Confirm Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Confirm new password"
                    required
                  />
                </div>

                {/* Password requirements */}
                <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                  <p className="font-medium mb-1.5">Password requirements:</p>
                  {passwordValidation.checks.filter(c => c.id !== 'maxLength').map(check => (
                    <div key={check.id} className="flex items-center gap-2">
                      {newPassword.length === 0 ? (
                        <div className="w-3.5 h-3.5 rounded-full border border-gray-300 dark:border-gray-600" />
                      ) : check.passed ? (
                        <svg className="w-3.5 h-3.5 flex-shrink-0 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-3.5 h-3.5 flex-shrink-0 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                      <span className={newPassword.length === 0 ? '' : check.passed ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}>
                        {check.label}
                      </span>
                    </div>
                  ))}
                  <div className="flex items-center gap-2">
                    {confirmPassword.length === 0 ? (
                      <div className="w-3.5 h-3.5 rounded-full border border-gray-300 dark:border-gray-600" />
                    ) : passwordsMatch ? (
                      <svg className="w-3.5 h-3.5 flex-shrink-0 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-3.5 h-3.5 flex-shrink-0 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    <span className={confirmPassword.length === 0 ? '' : passwordsMatch ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}>
                      Passwords match
                    </span>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button type="button" onClick={closePasswordModal} className="btn-secondary">Cancel</button>
                  <button
                    type="submit"
                    disabled={changingPassword || !passwordValidation.valid || !passwordsMatch}
                    className="btn-primary"
                  >
                    {changingPassword ? 'Updating...' : 'Update Password'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default PortalLayout
